/**
 * Analytics module DTOs (§12.5, §19, §15.2): client event batches, audience segments and opted-in exports,
 * lottery evidence exports and the audit log read surface.
 */
import { z } from 'zod';
import { IsoDateTime, Ulid, paginated } from './common.js';
import { AuditActorKind, EngagementTaskType, SocialPlatform } from './enums.js';
import { MoneyDto } from './money.js';
import { CLIENT_EMITTABLE_TOPICS, EventEnvelope, EVENT_PAYLOADS, SourcePlatform } from './events.js';
/** Browser-originated events; the API fills tenant, lottery, subject and server timestamp. */
export const ClientEventInput = z
    .object({
    topic: z.enum(CLIENT_EMITTABLE_TOPICS),
    clientTs: IsoDateTime,
    payload: z.record(z.string(), z.unknown()),
    placement: EventEnvelope.shape.placement,
    campaign: EventEnvelope.shape.campaign,
    /** Consent class the browser recorded the event under (D-903); MARKETING is never browser-emitted. */
    consentClass: z.enum(['OPERATIONAL', 'ANALYTICS', 'SPONSOR']).optional(),
})
    .superRefine((e, ctx) => {
    const schema = EVENT_PAYLOADS[e.topic];
    if (!schema)
        return;
    const r = schema.safeParse(e.payload);
    if (!r.success)
        ctx.addIssue({
            code: 'custom',
            path: ['payload'],
            message: r.error.issues[0]?.message ?? 'invalid payload',
        });
});
export const ClientEventBatchRequest = z.object({
    lotterySlug: z.string().max(64),
    entryId: Ulid.optional(),
    /** Participant consent state at send time (§12.5): ANALYTICS-class events are dropped unless `analytics` is true. */
    consent: z.object({ analytics: z.boolean().default(false) }).optional(),
    events: z.array(ClientEventInput).min(1).max(50),
});
// --- audience (§12.5) ------------------------------------------------------------------------------------
export const SegmentRule = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('ENTERED_AT_LEAST'), lotteries: z.number().int().min(1) }),
    z.object({ kind: z.literal('WON_PRIZE_CATEGORY'), category: z.string().max(32) }),
    z.object({ kind: z.literal('SOURCE_PLATFORM'), platform: SocialPlatform }),
    z.object({ kind: z.literal('ENTERED_LOTTERY'), lotteryId: Ulid }),
    z.object({ kind: z.literal('VARIANT_PREFERENCE'), axis: z.string().max(32), value: z.string().max(64) }),
    /** Consent-state filter (§12.5): exports still only ever contain OPTED_IN contacts whatever the segment says. */
    z.object({ kind: z.literal('CONSENT_STATE'), state: z.enum(['OPTED_IN', 'WITHDRAWN', 'NONE']) }),
]);
export const SegmentDto = z.object({
    segmentId: Ulid,
    name: z.string(),
    rules: z.array(SegmentRule),
    /** Counts are of opted-in contacts only. */
    optedInCount: z.number().int(),
    totalMatched: z.number().int(),
    updatedAt: IsoDateTime,
});
export const UpsertSegmentRequest = z.object({
    segmentId: Ulid.optional(),
    name: z.string().min(1).max(120),
    rules: z.array(SegmentRule).min(1),
});
export const AudienceExportRequest = z.object({
    segmentId: Ulid,
    purpose: z.enum(['CAMPAIGN', 'PRODUCT_LAUNCH', 'CRM_SYNC']),
    format: z.enum(['CSV', 'JSON']).default('CSV'),
    /** Free-text purpose note stored with the export history (campaign name, CRM target). */
    purposeNote: z.string().max(300).optional(),
    /** Exports contain only MARKETING-opted-in contacts; withdrawn consent suppresses (Appendix A). */
    acknowledgeOptInOnly: z.literal(true),
});
export const ExportJobDto = z.object({
    exportId: Ulid,
    kind: z.enum([
        'AUDIENCE',
        'WINNERS',
        'ENTRIES',
        'LEDGER',
        'FAIRNESS',
        'SPONSOR_REPORT',
        'EVIDENCE_PACK',
        'AUDIT',
        'CLAIMS',
        'RECONCILIATION',
    ]),
    status: z.enum(['QUEUED', 'RUNNING', 'READY', 'FAILED', 'EXPIRED']),
    requestedBy: Ulid,
    purpose: z.string().nullable(),
    lotteryId: Ulid.nullable().optional(),
    segmentId: Ulid.nullable().optional(),
    format: z.string().optional(),
    rows: z.number().int().nullable(),
    /** Contacts suppressed at export time: withdrawn consent or never opted in (PRIV-01 evidence). */
    suppressedCount: z.number().int().optional(),
    /** Short-lived signed URL; access is logged (§15.2). */
    downloadUrl: z.string().nullable(),
    expiresAt: IsoDateTime.nullable(),
    watermark: z.string().nullable(),
    createdAt: IsoDateTime,
});
export const LotteryExportType = z.enum([
    'entries',
    'winners',
    'ledger',
    'fairness',
    'evidence-pack',
    'reconciliation',
    'claims',
]);
// --- audit (`GET /v1/admin/audit`) ---------------------------------------------------------------------
export const AuditQuery = z.object({
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    actorId: z.string().max(80).optional(),
    actorKind: AuditActorKind.optional(),
    action: z.string().max(80).optional(),
    objectType: z.string().max(40).optional(),
    objectId: z.string().max(80).optional(),
});
export const AuditEntryDto = z.object({
    auditId: Ulid,
    tenantId: Ulid,
    at: IsoDateTime,
    actorKind: AuditActorKind,
    actorId: z.string(),
    action: z.string(),
    objectType: z.string(),
    objectId: z.string(),
    /** Redacted diff / details; never tokens, seeds before reveal, or raw identity values. */
    details: z.record(z.string(), z.unknown()),
    purpose: z.string().nullable(),
    /** Hash chain for tamper evidence. */
    prevHash: z.string(),
    hash: z.string(),
});
export const AuditPageDto = paginated(AuditEntryDto);
/** Live and next-day reporting tiles (§19 reporting cadence). */
export const LiveMetricsDto = z.object({
    lotteryId: Ulid,
    asOf: IsoDateTime,
    traffic: z.number().int(),
    entryStarts: z.number().int(),
    confirmedEntries: z.number().int(),
    paymentLagP95Ms: z.number().int().nullable(),
    queueHealth: z.enum(['GREEN', 'AMBER', 'RED']),
    errorRate: z.number().min(0),
    claimsOpen: z.number().int(),
    unmessagedWinners: z.number().int(),
});
// --- analytics agent extensions (D-1801…): events hygiene, RUM, dashboard, §19 metric definitions, reports,
// audience profiles, experiments, deletion workflow --------------------------------------------------------------
/** Payload keys a browser batch may never carry (§15.1, §17.3): stripped server-side, never persisted. */
export const CLIENT_EVENT_FORBIDDEN_KEYS = [
    'contact',
    'contactValue',
    'email',
    'phone',
    'phoneNumber',
    'handle',
    'displayName',
    'legalName',
    'name',
    'address',
    'idNumber',
    'token',
    'claimToken',
    'winToken',
    'secret',
    'password',
    'authorization',
    'cookie',
    'signature',
    'sig',
    'seed',
    'payoutDestination',
];
/** Values that look like contacts or tokens are redacted even under an allowed key. */
export const CLIENT_EVENT_REDACT_PATTERNS = [
    /^\+?\d[\d\s().-]{6,}\d$/, // phone-like
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/, // e-mail
    /^@[A-Za-z0-9._-]{2,}$/, // handle
    /^[A-Za-z0-9_-]{40,}$/, // bearer / claim token
];
/** `POST /v1/rum/vitals` beacon: route pattern only, never a concrete URL or identifier (§13.6, §20). */
export const RumVitalBeacon = z.object({
    name: z.enum(['LCP', 'INP', 'CLS', 'TTFB']),
    value: z.number().min(0).max(600_000),
    rating: z.enum(['good', 'needs-improvement', 'poor']),
    route: z
        .string()
        .min(1)
        .max(120)
        .regex(/^\/[A-Za-z0-9_:/.-]*$/, 'route pattern only'),
    navigationType: z.string().max(32).optional(),
    buildSha: z.string().max(64),
    device: z.enum(['mobile', 'tablet', 'desktop', 'unknown']).optional(),
});
// --- dashboard (§16.2) ------------------------------------------------------------------------------------
export const DashboardQuery = z.object({ lotteryId: Ulid.optional() });
/** `GET /v1/admin/dashboard`. Every section is nullable: null = "no data yet", never a zero that looks like a fact. */
export const DashboardDto = z.object({
    asOf: IsoDateTime,
    lotteryId: Ulid.nullable(),
    liveLotteries: z.number().int(),
    funnel: z
        .object({
        views: z.number().int(),
        entryStarts: z.number().int(),
        /** Eligible entries only — pending payments are not entries (§19). */
        confirmedEntries: z.number().int(),
        paymentPending: z.number().int(),
    })
        .nullable(),
    entriesBySource: z.array(z.object({ platform: SourcePlatform, count: z.number().int() })),
    paidReconciliation: z
        .object({
        succeeded: z.number().int(),
        pending: z.number().int(),
        exceptions: z.number().int(),
        collected: MoneyDto,
    })
        .nullable(),
    prizeLiability: z
        .object({ committed: MoneyDto, cap: MoneyDto.nullable(), unitsReserved: z.number().int() })
        .nullable(),
    claimSla: z
        .object({
        open: z.number().int(),
        overdue: z.number().int(),
        medianHoursToDecision: z.number().nullable(),
    })
        .nullable(),
    fulfillmentSla: z
        .object({ open: z.number().int(), exceptions: z.number().int(), overdue: z.number().int() })
        .nullable(),
    riskExceptions: z.object({ open: z.number().int(), highSeverity: z.number().int() }).nullable(),
    /** §1.3 guardrail: every unmessaged winner has an assigned admin and a reminder. */
    unmessagedWinners: z
        .object({ count: z.number().int(), unassigned: z.number().int(), oldestHours: z.number().nullable() })
        .nullable(),
    /** Hourly confirmed entries for the trend chart; empty when nothing happened yet. */
    entriesSeries: z.array(z.object({ at: IsoDateTime, confirmedEntries: z.number().int() })),
});
// --- §19 metric definitions ------------------------------------------------------------------------------
export const MetricArea = z.enum([
    'discovery',
    'funnel',
    'game_feel',
    'virality',
    'social',
    'sponsors',
    'revenue',
    'claims',
    'fulfillment',
    'integrity',
]);
export const MetricUnit = z.enum(['count', 'ratio', 'money', 'hours', 'seconds']);
const AREA_CAUTIONS = {
    discovery: 'Filter known bots.',
    funnel: 'Pending payments are not entries.',
    game_feel: 'Segment by mechanic, device and accessibility mode.',
    virality: 'Raw link clicks do not earn referral credit.',
    social: 'Follow tasks are attested, not API-verified.',
    sponsors: 'Identity suppression and viewability rule applied.',
    revenue: 'Derived from the ledger, not front-end events.',
    claims: 'Separate participant delay from platform and provider delay.',
    fulfillment: 'Every open exception has an owner.',
    integrity: 'Never optimize solely for more blocks.',
};
const def = (area, key, label, definition, unit = 'count') => ({ key, area, label, definition, caution: AREA_CAUTIONS[area], unit });
/** Every §19 metric with its definition, owner area and caution. Values come from `GET /v1/admin/metrics/:area`. */
export const METRIC_DEFINITIONS = [
    def('discovery', 'qualified_visits', 'Qualified visits', 'lottery_viewed events from distinct pseudonymous subjects, bot-filtered.'),
    def('discovery', 'landing_engagement', 'Landing engagement', 'rules_expanded events per qualified visit.', 'ratio'),
    def('discovery', 'source_platforms', 'Source platforms', 'Distinct source platforms with at least one qualified visit.'),
    def('funnel', 'entry_starts', 'Entry starts', 'entry_started events (browser) in the window.'),
    def('funnel', 'eligible_submissions', 'Eligible submissions', 'entry_submitted events whose entry later reached ELIGIBLE or SNAPSHOT_INCLUDED.'),
    def('funnel', 'payment_initialized', 'Payment initialized', 'payment_pending events (intent created at the provider).'),
    def('funnel', 'confirmed_entries', 'Confirmed entries', 'Entries in ELIGIBLE or SNAPSHOT_INCLUDED. A PENDING payment is never counted as an entry.'),
    def('funnel', 'dropoff_start_to_confirmed', 'Drop-off start → confirmed', '1 − confirmed entries ÷ entry starts.', 'ratio'),
    def('game_feel', 'reveal_completion_rate', 'Reveal completion', 'reveal_presented ÷ (reveal_presented + reveal_skipped).', 'ratio'),
    def('game_feel', 'reveal_skip_rate', 'Reveal skip rate', 'reveal_skipped ÷ (reveal_presented + reveal_skipped).', 'ratio'),
    def('game_feel', 'share_cards_generated', 'Share cards generated', 'Share links created from a win or receipt.'),
    def('game_feel', 'calm_mode_usage', 'Calm-mode usage', 'Share of reveals presented with energyLevel CALM or reduced motion.', 'ratio'),
    def('virality', 'shares', 'Shares', 'Tracked share links created.'),
    def('virality', 'qualified_referrals', 'Qualified referrals', 'Referral rows with a converted (qualifying) entry. Raw link clicks earn nothing.'),
    def('virality', 'converted_shares', 'Converted shares', 'Share links with at least one qualifying conversion.'),
    def('virality', 'bonus_entries', 'Bonus entries', 'Entries with source BONUS in ELIGIBLE / SNAPSHOT_INCLUDED.'),
    def('virality', 'k_factor', 'K-factor', 'Qualified referrals ÷ unique participants.', 'ratio'),
    def('social', 'winners_messaged', 'Winners messaged', 'WinnerContact rows marked MESSAGED or FOLLOWED_UP by an admin.'),
    def('social', 'tap_to_messaged_hours', 'Button-tap → messaged', 'Median hours between buttonTappedAt and messagedAt.', 'hours'),
    def('social', 'follows_and_joins', 'Follows and joins driven', 'QUALIFIED completions of FOLLOW and LINK_CHANNEL tasks (attested, not API-verified).'),
    def('social', 'marketing_opt_in_rate', 'Marketing opt-in rate', 'Participants with an active marketing consent ÷ participants.', 'ratio'),
    def('social', 'audience_growth', 'Audience growth', 'New opted-in marketing consents in the window.'),
    def('sponsors', 'viewable_impressions', 'Viewable impressions', 'sponsor_impression events with viewable=true.'),
    def('sponsors', 'sponsor_clicks', 'Sponsor clicks', 'sponsor_impression events with click=true.'),
    def('sponsors', 'samples_allocated', 'Samples allocated', 'SamplingAward rows with status AWARDED.'),
    def('sponsors', 'samples_claimed', 'Samples claimed', 'Claims on sampling wins that reached SUBMITTED or later.'),
    def('revenue', 'gross_entries', 'Gross entries', 'Σ credits to ENTRY_REVENUE from the ledger.', 'money'),
    def('revenue', 'fees', 'Provider fees', 'Σ debits to PROVIDER_FEES.', 'money'),
    def('revenue', 'refunds', 'Refunds', 'Σ debits to REFUNDS.', 'money'),
    def('revenue', 'chargebacks', 'Chargebacks', 'Σ debits to CHARGEBACKS.', 'money'),
    def('revenue', 'net_ledger_revenue', 'Net ledger revenue', 'Gross entries − fees − refunds − chargebacks.', 'money'),
    def('revenue', 'prize_cost', 'Prize cost', 'Σ debits to PRIZE_COST.', 'money'),
    def('revenue', 'lottery_roi', 'Lottery ROI', '(Net ledger revenue − prize cost) ÷ prize cost.', 'ratio'),
    def('claims', 'claims_started', 'Claims started', 'Claims whose token was exchanged for a session (OPENED or later).'),
    def('claims', 'claims_completed', 'Claims completed', 'Claims SUBMITTED or later.'),
    def('claims', 'verification_pass', 'Verification pass', 'VerificationCase decisions VERIFIED.'),
    def('claims', 'verification_more_info', 'Verification more-info', 'VerificationCase decisions MORE_INFO.'),
    def('claims', 'verification_reject', 'Verification reject', 'VerificationCase decisions REJECTED.'),
    def('claims', 'time_to_claim_hours', 'Time to claim', 'Median hours from wonAt to submittedAt.', 'hours'),
    def('claims', 'appeals', 'Appeals', 'Support cases in category appeal.'),
    def('fulfillment', 'time_to_paid_hours', 'Time to paid/delivered', 'Median hours from approvedAt to fulfillment completedAt.', 'hours'),
    def('fulfillment', 'sla_breaches', 'SLA breaches', 'Fulfillments past slaDueAt and not COMPLETED.'),
    def('fulfillment', 'exception_age_hours', 'Exception age', 'Age of the oldest fulfillment in EXCEPTION.', 'hours'),
    def('integrity', 'blocked_attempts', 'Blocked attempts', 'Rate-limited, replayed or cross-tenant requests counted by the API.'),
    def('integrity', 'false_positive_reviews', 'False-positive reviews', 'Risk flags reviewed and cleared.'),
    def('integrity', 'redraws', 'Redraws', 'Redraw rows (each with reason, new seed commitment and draw id).'),
    def('integrity', 'reconciliation_mismatches', 'Reconciliation mismatches', 'ReconciliationException rows OPEN or INVESTIGATING.'),
    def('integrity', 'fairness_verification_failures', 'Fairness verification failures', 'Draws whose verifier reproduction failed.'),
];
export const metricDefinitionsFor = (area) => METRIC_DEFINITIONS.filter((m) => m.area === area);
export const MetricValueDto = z.object({
    key: z.string(),
    label: z.string(),
    definition: z.string(),
    caution: z.string(),
    unit: MetricUnit,
    /** null = no data in the window (never a fabricated zero). Money values are also carried in `money`. */
    value: z.number().nullable(),
    money: MoneyDto.nullable().optional(),
});
export const MetricsAreaQuery = z.object({
    lotteryId: Ulid.optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
});
export const MetricsAreaDto = z.object({
    area: MetricArea,
    lotteryId: Ulid.nullable(),
    asOf: IsoDateTime,
    window: z.object({ from: IsoDateTime, to: IsoDateTime }),
    caution: z.string(),
    metrics: z.array(MetricValueDto),
});
// --- reports (§19 cadence, §23.2 hypercare) ------------------------------------------------------------
export const SponsorDeliveryRowDto = z.object({
    sponsorId: Ulid,
    sponsorName: z.string(),
    viewableImpressions: z.number().int(),
    clicks: z.number().int(),
    samplesAllocated: z.number().int(),
    samplesClaimed: z.number().int(),
});
/** `GET /v1/admin/lotteries/:id/report` — campaign close (§19 "Campaign close"). */
export const CampaignReportDto = z.object({
    lotteryId: Ulid,
    slug: z.string(),
    title: z.string(),
    status: z.string(),
    generatedAt: IsoDateTime,
    cohortFunnel: z.object({
        views: z.number().int(),
        entryStarts: z.number().int(),
        eligibleSubmissions: z.number().int(),
        paymentInitialized: z.number().int(),
        confirmedEntries: z.number().int(),
        uniqueParticipants: z.number().int(),
        bySource: z.array(z.object({ platform: SourcePlatform, views: z.number().int(), confirmedEntries: z.number().int() })),
    }),
    incrementalReferrals: z.object({
        shares: z.number().int(),
        qualifiedReferrals: z.number().int(),
        convertedShares: z.number().int(),
        bonusEntries: z.number().int(),
        kFactor: z.number().nullable(),
    }),
    /** Net financials from the ledger; null when the lottery has no ledger activity (free lotteries). */
    netFinancials: z
        .object({
        currency: z.string(),
        grossEntries: MoneyDto,
        fees: MoneyDto,
        refunds: MoneyDto,
        chargebacks: MoneyDto,
        netRevenue: MoneyDto,
        prizeCost: MoneyDto,
        roi: z.number().nullable(),
    })
        .nullable(),
    audienceGrowth: z.object({
        newParticipants: z.number().int(),
        marketingOptIns: z.number().int(),
        optInRate: z.number().nullable(),
        withdrawals: z.number().int(),
    }),
    sponsorDelivery: z.array(SponsorDeliveryRowDto),
    claims: z.object({
        wins: z.number().int(),
        claimsStarted: z.number().int(),
        claimsSubmitted: z.number().int(),
        approved: z.number().int(),
        rejected: z.number().int(),
        moreInfo: z.number().int(),
        paidOrDelivered: z.number().int(),
        medianHoursToClaim: z.number().nullable(),
    }),
    /** Template lessons are a human input; the report leaves the slot so the retrospective has a home. */
    lessons: z.array(z.string()),
});
export const NextBusinessDayReportQuery = z.object({
    /** Report day (YYYY-MM-DD, lottery/tenant timezone); defaults to yesterday. */
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    lotteryId: Ulid.optional(),
});
/** `GET /v1/admin/reports/next-business-day` (§19 "Next business day"). */
export const NextBusinessDayReportDto = z.object({
    date: z.string(),
    window: z.object({ from: IsoDateTime, to: IsoDateTime }),
    generatedAt: IsoDateTime,
    reconciliation: z.object({
        runs: z.number().int(),
        openExceptions: z.number().int(),
        blocksDraw: z.boolean(),
    }),
    channelQuality: z.array(z.object({
        platform: SourcePlatform,
        entryStarts: z.number().int(),
        confirmedEntries: z.number().int(),
        conversionRate: z.number().nullable(),
    })),
    claimsSla: z.object({
        decided: z.number().int(),
        withinSla: z.number().int(),
        breaches: z.number().int(),
        medianHours: z.number().nullable(),
    }),
    fulfillmentSla: z.object({
        completed: z.number().int(),
        overdue: z.number().int(),
        exceptions: z.number().int(),
    }),
    sponsorDelivery: z.array(SponsorDeliveryRowDto),
    riskOutcomes: z.object({
        blockedAttempts: z.number().int(),
        openReviews: z.number().int(),
        redraws: z.number().int(),
    }),
});
export const HypercareWindowId = z.enum(['FIRST_24H', 'DAYS_2_7', 'DAYS_8_14', 'DAYS_15_30']);
export const HypercareReportQuery = z.object({ lotteryId: Ulid.optional() });
/** `GET /v1/admin/reports/hypercare` (§23.2 first thirty days). */
export const HypercareReportDto = z.object({
    lotteryId: Ulid.nullable(),
    /** First publication across the scope; null before anything went live. */
    launchedAt: IsoDateTime.nullable(),
    asOf: IsoDateTime,
    currentWindow: HypercareWindowId.nullable(),
    windows: z.array(z.object({
        id: HypercareWindowId,
        label: z.string(),
        focus: z.string(),
        exitEvidence: z.string(),
        status: z.enum(['UPCOMING', 'ACTIVE', 'DONE']),
        metrics: z.array(MetricValueDto),
    })),
});
// --- audience console (§12.5, §16.2) ----------------------------------------------------------------------
export const MarketingConsentState = z.enum(['OPTED_IN', 'WITHDRAWN', 'NONE']);
export const SegmentPreviewDto = z.object({
    segmentId: Ulid,
    totalMatched: z.number().int(),
    /** Contacts an export would contain right now. */
    optedInCount: z.number().int(),
    /** Matched contacts suppressed by a withdrawal. */
    withdrawnSuppressed: z.number().int(),
    evaluatedAt: IsoDateTime,
});
export const ParticipantListQuery = z.object({
    lotteryId: Ulid.optional(),
    platform: SocialPlatform.optional(),
    consent: MarketingConsentState.optional(),
    /** Participant id (exact) or display-name prefix. */
    q: z.string().max(80).optional(),
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
/** Masked profile row (§16.3: lists default to masked contact data). */
export const ParticipantRowDto = z.object({
    participantId: Ulid,
    displayName: z.string(),
    contactMasked: z.string(),
    contactKind: z.string(),
    sourcePlatform: SocialPlatform.nullable(),
    lotteriesEntered: z.number().int(),
    prizesWon: z.number().int(),
    marketing: MarketingConsentState,
    anonymised: z.boolean(),
    createdAt: IsoDateTime,
});
export const ParticipantPageDto = paginated(ParticipantRowDto);
export const EngagementResultsQuery = z.object({ lotteryId: Ulid.optional() });
/** `GET /v1/admin/audience/engagement` — §12.5 engagement dashboard (task results, follows and joins driven). */
export const EngagementResultsDto = z.object({
    lotteryId: Ulid.nullable(),
    asOf: IsoDateTime,
    tasks: z.array(z.object({
        taskId: Ulid,
        lotteryId: Ulid,
        type: EngagementTaskType,
        title: z.string(),
        platform: SocialPlatform.nullable(),
        completions: z.number().int(),
        qualified: z.number().int(),
        pending: z.number().int(),
        rejected: z.number().int(),
        bonusEntries: z.number().int(),
    })),
    referrals: z.object({ total: z.number().int(), converted: z.number().int() }),
    shares: z.object({ links: z.number().int(), clicks: z.number().int(), conversions: z.number().int() }),
    followsAndJoins: z.number().int(),
    winnersByPlatform: z.array(z.object({
        platform: SocialPlatform,
        notMessaged: z.number().int(),
        buttonTapped: z.number().int(),
        messaged: z.number().int(),
        followedUp: z.number().int(),
    })),
});
/** Admin-recorded withdrawal (§12.5 "withdrawal suppresses future exports"; operational messages stay separate). */
export const AdminWithdrawConsentRequest = z.object({
    purpose: z.enum(['MARKETING', 'SPONSOR_COMMUNICATION', 'WINNER_PUBLICITY']).default('MARKETING'),
    reason: z.string().min(3).max(300),
});
/** §17.3 deletion workflow: profile, channel identities and media; legal holds respected; suppression kept. */
export const EraseParticipantRequest = z.object({
    /** Typed confirmation (§16.3): must equal the path participant id. */
    confirmParticipantId: Ulid,
    reason: z.string().min(10).max(500),
});
export const EraseParticipantResponse = z.object({
    participantId: Ulid,
    status: z.enum(['COMPLETED', 'BLOCKED_LEGAL_HOLD']),
    erased: z.object({
        profile: z.boolean(),
        channelIdentities: z.number().int(),
        mediaObjects: z.number().int(),
        analyticsRows: z.number().int(),
    }),
    legalHoldRefs: z.array(z.string()),
    /** Always true: the minimized ConsentWithdrawal keyed by contact hash survives erasure. */
    suppressionKept: z.literal(true),
    erasedAt: IsoDateTime,
});
// --- exports --------------------------------------------------------------------------------------------
export const ExportListQuery = z.object({
    kind: ExportJobDto.shape.kind.optional(),
    lotteryId: Ulid.optional(),
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const ExportPageDto = paginated(ExportJobDto);
export const ExportAccessDto = z.object({
    accessId: Ulid,
    exportId: Ulid,
    userId: z.string(),
    action: z.enum(['download', 'view', 'revoke', 'expired_attempt']),
    at: IsoDateTime,
});
/** `GET /v1/admin/audit/export` — same filters as the audit list; produces an async AUDIT export job. */
export const AuditExportQuery = AuditQuery;
/** Audit & incidents console: severity list derived from escalations, reconciliation exceptions and draw checks. */
export const IncidentDto = z.object({
    incidentId: z.string(),
    severity: z.enum(['SEV1', 'SEV2', 'SEV3']),
    kind: z.enum([
        'support_escalation',
        'reconciliation_exception',
        'draw_check_failure',
        'fulfillment_exception',
    ]),
    title: z.string(),
    at: IsoDateTime,
    status: z.string(),
    objectType: z.string(),
    objectId: z.string(),
});
export const IncidentListDto = z.object({ asOf: IsoDateTime, items: z.array(IncidentDto) });
// --- experiments (§19 experimentation rule, §2.4) --------------------------------------------------------
export const EXPERIMENT_ALLOWED_DIMENSIONS = [
    'HERO_COMPOSITION',
    'CTA_WORDING',
    'FIELD_ORDER',
    'REVEAL_DURATION',
    'SPONSOR_PLACEMENT',
    'CLAIM_GUIDANCE',
];
export const ExperimentDimension = z.enum(EXPERIMENT_ALLOWED_DIMENSIONS);
/** Words that mark a forbidden test subject (§19: odds, price, eligibility, outcome, consent, claim rights, refunds, urgency). */
export const EXPERIMENT_FORBIDDEN_PATTERNS = [
    /odds/i,
    /price|cost|fee/i,
    /eligib/i,
    /outcome|winner|result/i,
    /consent/i,
    /claim[._ -]?(right|window|deadline)/i,
    /refund/i,
    /urgen|scarcit|countdown|timer|deadline/i,
    /seed|pool|ticket/i,
];
export const experimentChangeIsForbidden = (key, value) => {
    const text = `${key} ${typeof value === 'string' ? value : ''}`;
    return EXPERIMENT_FORBIDDEN_PATTERNS.some((p) => p.test(text));
};
export const ExperimentVariant = z.object({
    key: z
        .string()
        .min(1)
        .max(32)
        .regex(/^[a-z0-9_-]+$/),
    /** Traffic share in percent; variants must sum to 100. */
    weight: z.number().int().min(1).max(100),
    /** Presentation changes only, e.g. { "hero.layout": "prize-first", "cta.label": "Enter now" }. */
    changes: z.record(z.string().max(64), z.union([z.string().max(200), z.number(), z.boolean()])),
});
export const CreateExperimentRequest = z
    .object({
    key: z
        .string()
        .min(3)
        .max(48)
        .regex(/^[a-z0-9][a-z0-9-]*$/),
    name: z.string().min(3).max(120),
    lotteryId: Ulid.optional(),
    dimension: ExperimentDimension,
    hypothesis: z.string().max(500).optional(),
    primaryMetric: z.enum(['task_success', 'comprehension', 'conversion']).default('task_success'),
    variants: z.array(ExperimentVariant).min(2).max(6),
})
    .superRefine((e, ctx) => {
    const total = e.variants.reduce((s, v) => s + v.weight, 0);
    if (total !== 100)
        ctx.addIssue({ code: 'custom', path: ['variants'], message: 'variant weights must sum to 100' });
    const keys = new Set(e.variants.map((v) => v.key));
    if (keys.size !== e.variants.length)
        ctx.addIssue({ code: 'custom', path: ['variants'], message: 'variant keys must be unique' });
    e.variants.forEach((v, i) => {
        for (const [k, val] of Object.entries(v.changes)) {
            if (experimentChangeIsForbidden(k, val))
                ctx.addIssue({
                    code: 'custom',
                    path: ['variants', i, 'changes', k],
                    message: 'experiments may not touch odds, price, eligibility, outcome, consent wording, claim rights, refund policy or urgency (§19)',
                });
        }
    });
});
export const ExperimentDto = z.object({
    experimentId: Ulid,
    key: z.string(),
    name: z.string(),
    lotteryId: Ulid.nullable(),
    dimension: ExperimentDimension,
    hypothesis: z.string().nullable(),
    primaryMetric: z.enum(['task_success', 'comprehension', 'conversion']),
    variants: z.array(ExperimentVariant),
    status: z.enum(['DRAFT', 'RUNNING', 'STOPPED']),
    createdAt: IsoDateTime,
    startedAt: IsoDateTime.nullable(),
    stoppedAt: IsoDateTime.nullable(),
});
export const UpdateExperimentRequest = z.object({ status: z.enum(['RUNNING', 'STOPPED']) });
//# sourceMappingURL=analytics.js.map